import type { ComputedRef, UnwrapNestedRefs } from 'vue'
import type { BookingLinkByHash, BookingLinkPrefill, CarrierBookingDetail } from '@apiTypes'
import { BookingLinkStatusEnum } from '@apiTypes'
import { useAppToast } from '@/composables/ui/app-toast'
import { isCarrierAccessError } from '@/composables/session/carrier-access'
import { useOnlineStatus } from '@/composables/push/online-status'
import { useCarrierBookingsStore } from '@store/carrier-bookings'
import { isNotFoundError } from '@/utils/http.utils'
import { useBookingLinkPush } from '@/composables/push/push-events'
import { PushEventName } from '@/types/push-events'
import { usePushRefresh } from '@/composables/push/push-refresh'

export enum BookingLinkPageKind {
  Form = 'form',
  Booked = 'booked',
  Unavailable = 'unavailable',
}

export type BookingLinkPageState
  = | { kind: BookingLinkPageKind.Form; prefill: BookingLinkPrefill }
    | { kind: BookingLinkPageKind.Booked; prefill: BookingLinkPrefill }

    /** `null` for an unknown or deleted hash. */
    | { kind: BookingLinkPageKind.Unavailable; status: BookingLinkStatusEnum | null }

function nullOnNotFound<T>(promise: Promise<{ data: T }>): Promise<T | null> {
  return promise.then(({ data }) => data).catch((error: unknown) => {
    if (isNotFoundError(error))
      return null

    throw error
  })
}

/** `null` for an unknown or deleted hash. */
export function loadBookingLink(hash: string): Promise<BookingLinkByHash | null> {
  return nullOnNotFound(useCarrierBookingsStore().fetchBookingLinkByHash(hash))
}

/** The booking made through the link; `null` for an unknown hash or a link not used yet. */
export function loadBookingLinkBooking(hash: string): Promise<CarrierBookingDetail | null> {
  return nullOnNotFound(useCarrierBookingsStore().fetchBookingLinkBooking(hash))
}

export function bookingLinkPageState(link: BookingLinkByHash | null): BookingLinkPageState {
  if (link?.status === BookingLinkStatusEnum.SENT && link.prefill)
    return { kind: BookingLinkPageKind.Form, prefill: link.prefill }

  if (link?.status === BookingLinkStatusEnum.BOOKED && link.prefill)
    return { kind: BookingLinkPageKind.Booked, prefill: link.prefill }

  return { kind: BookingLinkPageKind.Unavailable, status: link?.status ?? null }
}

type UseCarrierBookingLinkPageRefs = {
  state: ComputedRef<BookingLinkPageState>
  reload: () => Promise<void>
}

export type UseCarrierBookingLinkPageReturn = UnwrapNestedRefs<UseCarrierBookingLinkPageRefs>

export function useCarrierBookingLinkPage(
  hash: () => string,
  preloaded: () => BookingLinkByHash | null | undefined,
): UseCarrierBookingLinkPageReturn {
  const { t } = useI18n()
  const toast = useAppToast()

  const link = ref<BookingLinkByHash | null>(preloaded() ?? null)
  const pushEvents = useBookingLinkPush(hash, PushEventName.BookingLink)

  async function reload() {
    try {
      link.value = await loadBookingLink(hash())
    }
    catch (error) {
      // The global interceptor already redirects a carrier who lost access.
      if (!isCarrierAccessError(error))
        toast.error(t('Failed to load the booking link. Please try again.'))
    }
  }

  usePushRefresh(pushEvents, reload)
  useOnlineStatus(reload)

  return reactive({
    state: computed(() => bookingLinkPageState(link.value)),
    reload,
  })
}
